<template>
    <v-form ref="form" v-model="valid" data-cy="discounts-settings">
        <v-row dense>
            <v-spacer />
            <v-switch
                v-model="formData.active"
                class="ml-auto mr-4 width-max-content"
                :label="formData.active ? $t('active') : $t('inactive')"
                data-cy="status"
                hide-details
            />
        </v-row>
        <v-row dense class="mb-5">
            <v-col cols="12" sm="6" md="4" order="1" order-sm="0">
                <v-text-field
                    v-model="formData.name"
                    :label="`${$t('discount_name')}*`"
                    data-cy="discount-name"
                    hide-details="auto"
                    :rules="[requiredRule('name')]"
                />
            </v-col>
            <v-col cols="12" sm="6" md="8" order="0">
                <v-radio-group v-model="formData.discountType" inline hide-details class="mb-4 mb-sm-0">
                    <v-radio
                        v-for="discountType in useDiscountTypeItems"
                        :key="discountType.value"
                        :value="discountType.value"
                        :label="discountType.title"
                    />
                </v-radio-group>
            </v-col>
        </v-row>
        <div>{{ $t('discount_amount') }}*</div>
        <v-row no-gutters class="mb-5 ga-2 align-start">
            <v-text-field
                v-model="formData.amount"
                data-cy="discount-amount"
                hide-details="auto"
                type="number"
                hide-spin-buttons
                :rules="[requiredRule('discount_amount'), moreThanZero('discount_amount')]"
                max-width="100"
            />
            <v-select
                v-model="formData.currency"
                :items="useCurrencies"
                max-width="100"
                hide-details
                data-cy="discount-currency"
            />
            <v-select
                v-if="formData.currency"
                v-model="formData.applicationType"
                :items="useApplicationType"
                max-width="200"
                hide-details
                data-cy="discount-application-tType"
            />
        </v-row>
        <div class="text-subtitle-1">{{ $t('Discount conditions') }}</div>
        <div class="pb-8">
            <SecondLevelTabs :tabs="tabs" translation-key="discount_tabs">
                <template v-slot:promo_code>
                    <template v-if="formData.promoCode">
                        <v-radio-group v-model="formData.promoCode.saleDiscountPromoCodeType" class="mt-2">
                            <v-radio
                                v-for="promo in useSaleDiscountPromoCodeType"
                                :key="promo.value"
                                :value="promo.value"
                                :label="promo.title"
                                :data-cy="promo.value"
                            />
                        </v-radio-group>
                        <v-row
                            v-if="formData.promoCode.saleDiscountPromoCodeType !== 'IGNORED'"
                            no-gutters
                            class="ml-0 ml-sm-2 ga-2 align-center"
                        >
                            <v-text-field
                                v-model="formData.promoCode.promoCodeValue"
                                data-cy="promo-code-value"
                                hide-details="auto"
                                :label="
                                    formData.promoCode.saleDiscountPromoCodeType === 'SHARED'
                                        ? `${$t('shared_promo_code')}*`
                                        : `${$t('discount_tabs.promo_code')}*`
                                "
                                :rules="[
                                    requiredRule(
                                        formData.promoCode.saleDiscountPromoCodeType === 'SHARED'
                                            ? 'shared_promo_code'
                                            : 'discount_tabs.promo_code'
                                    ),
                                ]"
                                max-width="300"
                                min-width="150"
                            />
                            <v-btn
                                v-if="formData.promoCode.saleDiscountPromoCodeType === 'USED'"
                                data-cy="generate-promo-code"
                                variant="text"
                                @click="generate"
                            >
                                {{ $t('generate') }}
                            </v-btn>
                        </v-row>
                    </template>
                </template>
                <template v-slot:product>
                    <div v-for="productType in productTypes" :key="productType">
                        <v-checkbox
                            v-model="checkedProductTypes[productType]"
                            hide-details
                            :label="$t(`serviceType.${productType}`)"
                            density="compact"
                            @update:model-value="handleCheckboxChange(productType)"
                        />
                        <template v-if="!!getConditions(productType)">
                            <ConditionsSettings
                                v-model="getConditions(productType).conditions"
                                :condition-value-map="getConditionValueMap(productType)"
                                class="pl-0 pl-sm-8"
                            />
                        </template>
                    </div>
                </template>
                <template v-slot:sales_channels>
                    <v-row no-gutters>
                        <v-col cols="12" md="5">
                            <v-data-table
                                v-model="formData.selectedSalesChannels"
                                :headers="useHeaders"
                                :items="useSalesChannels"
                                disable-sort
                                density="compact"
                                :items-per-page="-1"
                                hide-default-footer
                                show-select
                            />
                        </v-col>
                    </v-row>
                </template>
                <template v-slot:membership_level>
                    <v-row no-gutters>
                        <v-col cols="12" md="5">
                            <v-data-table
                                v-model="formData.selectedMemberLevelIds"
                                :headers="useMemberShipLevelHeaders"
                                :items="membershipLevels"
                                disable-sort
                                density="compact"
                                :items-per-page="-1"
                                hide-default-footer
                                show-select
                            >
                                <template v-slot:item.code="{item}">
                                    {{ $t(`crm_membership_level.${item.code}`) }}
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </template>
            </SecondLevelTabs>
        </div>
        <v-row class="pa-4 gc-4">
            <v-btn :disabled="isLoading || !valid" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn :disabled="isLoading" variant="text" data-cy="cancel" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    const props = defineProps<{
        discountId?: number
        coryDiscountId?: number
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const initDefaultData = () => ({
        active: true,
        name: null,
        amount: null,
        currency: null,
        applicationType: 'PER_NIGHT',
        discountType: 'HOT_DEAL',
        selectedMemberLevelIds: [],
        selectedSalesChannels: [],
        productConditions: [],
        promoCode: {
            promoCodeValue: null,
            saleDiscountPromoCodeType: 'IGNORED',
        },
    })

    const isLoading = ref<boolean>(false)
    const valid = ref<boolean>(false)
    const formData = ref({})
    const tabs = ['promo_code', 'product', 'sales_channels', 'membership_level']
    const {membershipLevels} = useMembershipLevels()
    const productTypes = ['ACCOMMODATION', 'FLIGHT', 'TRANSFER', 'EXCURSION', 'OWN_EXTRA_SERVICE_PRODUCT_TYPE']
    const checkedProductTypes = ref<{[key: string]: boolean}>({})
    const salesConditions = ref([])

    const {$api, $toast, $conf} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.discountId || props.coryDiscountId) await loadData()
        else formData.value = initDefaultData()

        if (!supplierStore.salesConditions.length) await supplierStore.loadSalesConditions()
        salesConditions.value = supplierStore.salesConditions

        productTypes.forEach(productType => {
            checkedProductTypes.value[productType] = !!getConditions(productType)
        })
    })

    const getConditionValueMap = (productType: string) => {
        return salesConditions.value?.find(({productConditionType}) => productConditionType === productType)
            ?.conditionValueMap
    }

    async function loadData(): Promise<void> {
        isLoading.value = true
        try {
            formData.value = await $api.discount.get({id: props.discountId || props.coryDiscountId})
            if (props.coryDiscountId) {
                delete formData.value.id
                formData.value.name = `Copy of ${formData.value.name}`
                if (formData.value.promoCode.saleDiscountPromoCodeType === 'USED') {
                    formData.value.promoCode.promoCodeValue = null
                }
            }

            formData.value.productConditions = formData.value.productConditions?.map(
                ({id, conditions, valueType, ...rest}) => {
                    if (conditions) {
                        conditions = conditions.map(condition => {
                            delete condition.id
                            if (condition.valueType === 'COMPOSITE_LOCATION') {
                                if (!condition?.cityId) condition.cityId = condition.countryId
                            }
                            if (condition.valueType === 'HOTEL_NAME') {
                                condition.resolvedValue = condition.resolvedValue?.split(',')[0]
                            }
                            return condition
                        })
                    }
                    return {...rest, conditions, valueType}
                }
            )
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        isLoading.value = true
        try {
            if (props.discountId) {
                await $api.discount.put(props.discountId, formData.value)
            } else {
                await $api.discount.post(formData.value)
            }
            $toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message)
        } finally {
            isLoading.value = false
        }
    }

    const useDiscountTypeItems = computed(() => {
        const discounts = ['HOT_DEAL', 'PERCENT', 'SALE']

        return discounts.map(discount => ({
            title: t(`discountType.${discount}`),
            value: discount,
        }))
    })

    const useApplicationType = computed(() => {
        const applicationType = ['PER_NIGHT', 'ONE_TIME']
        return applicationType.map(type => ({
            title: t(`applicationType.${type}`),
            value: type,
        }))
    })

    const useCurrencies = computed(() => {
        const currencies = Object.values($conf.public.currency.available)
            .filter(currency => Object.keys(runtimeStore.exchangeRates).includes(currency))
            .map(currency => ({
                title: currency,
                value: currency,
            }))

        return [{title: '%', value: null}, ...currencies]
    })

    const useMemberShipLevelHeaders = computed(() => {
        return [{title: t('supplier.all'), value: 'code'}]
    })

    const useHeaders = computed(() => {
        return [{title: t('supplier.all'), value: 'title'}]
    })

    const useSalesChannels = computed(() => {
        const salesChannels = ['DIRECT_SALES', 'CORPORATOR', 'AGENCY', 'TOUR_OPERATOR', 'B2C']
        return salesChannels.map(value => ({id: value, title: t(`salesChannelsType.${value}`)}))
    })

    const useSaleDiscountPromoCodeType = computed(() => {
        const promoCodeType = ['IGNORED', 'USED', 'SHARED']
        return promoCodeType.map(type => ({
            title: t(`saleDiscountPromoCodeType.${type}`),
            value: type,
        }))
    })

    const generate = async () => {
        try {
            const {object: promoCode} = await $api.promoCode.get()
            formData.value.promoCode.promoCodeValue = promoCode
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const getConditions = (productType: string) => {
        if (!formData.value?.productConditions?.length) return null
        return formData.value.productConditions.find(item => item.productType === productType)
    }

    const handleCheckboxChange = (productType: string) => {
        const index = formData.value.productConditions.findIndex(item => item.productType === productType)
        if (checkedProductTypes.value[productType]) {
            if (index === -1) {
                formData.value.productConditions.push({
                    productType: productType,
                    conditions: [],
                })
            }
        } else {
            if (index !== -1) {
                formData.value.productConditions.splice(index, 1)
            }
        }
    }
</script>
